import { useState, type ReactNode } from "react";
import { C } from "@bd/content";
import { dateOf, decayWeeks, LINE_LABEL, lineOptions, money, palletCapacity, palletsUsed, pitchHeadStart, priceIndex, salesWhy, type LineKind, type Product, cafeReadout } from "@bd/sim";
import { Icon, Sheet, Sparkline, Tabs, Why } from "@bd/ui-kit";
import { me, useGame } from "../store";
import { cover, wonAward } from "../ui/Hud";
import { ScoreBadge } from "./Reviews";
import { t, tp } from "../i18n";

/** Two small glyphs the shared icon set doesn't have: a cart (copies sold) and a clock (time to make). */
const GLYPH = {
  sold: <><path d="M3 4h2.5l2.2 11h10.6L21 8H7" /><circle cx="9" cy="19.5" r="1.5" /><circle cx="17" cy="19.5" r="1.5" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
};
export function Glyph({ name, size = 14 }: { name: keyof typeof GLYPH; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flex: "none" }}>{GLYPH[name]}</svg>;
}

/** A value with its icon; the word is only in the tooltip ("box 103K"). */
export function IconVal({ icon, glyph, label, children, className }: { icon?: string; glyph?: keyof typeof GLYPH; label: string; children: ReactNode; className?: string }) {
  return (
    <span className={`ival${className ? ` ${className}` : ""}`} title={label}>
      <span className="ival-ic">{glyph ? <Glyph name={glyph} /> : <Icon name={icon ?? "info"} size={14} />}</span>
      <span className="num">{children}</span>
    </span>
  );
}

/** "7,902" up to 10K, then "79K", "1.2M". */
export function short(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1).replace(/\.0$/, "")}M`;
  if (n >= 1e4) return `${Math.round(n / 1e3)}K`;
  return n.toLocaleString("en-US");
}

export function Products() {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const co = me(game)!;
  // a game café shows how much its regulars really want to play each game again
  const cafe = cafeReadout(co);
  const [tab, setTab] = useState<"live" | "needs" | "stock" | "all">("live");
  const live = (p: Product) => p.launched && !p.retired && (p.stock > 0 || game.week - p.releaseWeek < decayWeeks(p) * 3 || p.evergreen);
  // needs you: sold out while it still sells, or running low with no reprint on order
  const weeklyNow = (p: Product) => p.weekly.at(-1) ?? 0;
  // (a game still selling a real number each week, not an old evergreen ticking over at a few copies)
  const needs = (p: Product) => p.launched && !p.retired && !p.orders.length && weeklyNow(p) >= 25 && p.stock < weeklyNow(p) * 4;
  // old stock: a year on sale and more than a year of sales sitting in the warehouse
  const oldStock = (p: Product) => p.launched && !p.retired && !p.clearance && game.week - p.releaseWeek > 52 && p.stock > Math.max(500, weeklyNow(p) * 52);
  const pick = { live: (p: Product) => live(p) || !p.launched, needs, stock: oldStock, all: () => true }[tab];
  const list = co.products.filter(pick).slice().reverse();
  const counts = { needs: co.products.filter(needs).length, stock: co.products.filter(oldStock).length };
  const booths = (co.booths ?? []).length;
  const imprints = (co.imprints ?? []).length;
  const offers = (co.licenceOffers ?? []).length + (co.pitches ?? []).length;
  return (
    <Sheet title={t("Your games")} onClose={() => set({ screen: null })} wide drawer testId="products">
      <div className="games-top">
        <div className="games-tabs">
          <Tabs tabs={[{ id: "live", label: t("On sale") }, { id: "needs", label: counts.needs ? `${t("Needs you")} · ${counts.needs}` : t("Needs you") }, { id: "stock", label: counts.stock ? `${t("Old stock")} · ${counts.stock}` : t("Old stock") }, { id: "all", label: t("All") }]} value={tab} onChange={setTab} />
        </div>
        <span className="games-links">
          <button className="btn small" onClick={() => set({ screen: { kind: "conventions" } })} data-testid="open-conventions"><Icon name="calendar" size={15} />{t("Conventions")}{booths ? ` · ${booths}` : ""}</button>
          {(imprints > 0 || co.level === "studio" || co.level === "hq") && <button className="btn small" onClick={() => set({ screen: { kind: "imprints" } })} data-testid="open-imprints"><Icon name="layers" size={15} />{t("Imprints")}{imprints ? ` · ${imprints}` : ""}</button>}
        </span>
      </div>
      {tab === "stock" && counts.stock > 1 && (
        <div className="row" style={{ margin: "8px 0", justifyContent: "flex-end" }}>
          <button className="btn small" onClick={() => { for (const p of co.products.filter(oldStock)) void cmd({ type: "clearance", productId: p.id }); }} title={t("Over a year of sales in the warehouse, costing storage every week.")} data-testid="clear-all">{t("Clear out all {n}", { n: counts.stock })}</button>
        </div>
      )}
      {offers > 0 && <span className="caps games-offers-h">{t("Offers")}</span>}
      {(co.licenceOffers ?? []).length > 0 && (
        <div className="stack games-offers" data-testid="licence-offers">
          {co.licenceOffers.map((o) => {
            const L = C.licence[o.id];
            const fee = money(L.fee * priceIndex(game.week) * (o.feeMult ?? 1));
            return (
              <div key={o.id} className="card offer">
                <span className="offer-art" aria-hidden><Icon name="doc" size={20} /></span>
                <span className="stack offer-main">
                  <strong title={t("{theme} game for {audience}", { theme: t(C.theme[L.theme].name), audience: t(C.audience[L.audience].name) })}>{t(L.name)}</strong>
                  <span className="ivals">
                    <span>{t(C.audience[L.audience].name)}</span>
                    <IconVal icon="coin" label={o.feeMult ? `${t("Fee")} ${t("(you outbid a rival)")}` : t("Fee")}>{fee}</IconVal>
                    <IconVal glyph="clock" label={tp(L.deadlineWeeks, "{n} week to make it", "{n} weeks to make it")}>{t("{n} wk", { n: L.deadlineWeeks })}</IconVal>
                    <IconVal icon="calendar" label={t("offer ends {date}", { date: dateLabel(o.expires) })}>{dateLabel(o.expires)}</IconVal>
                  </span>
                </span>
                <button className="btn small primary" onClick={() => set({ screen: { kind: "design", licence: L.id } })} data-testid="take-licence">{t("Make")}</button>
              </div>
            );
          })}
        </div>
      )}
      {(co.pitches ?? []).length > 0 && (
        <div className="stack games-offers" data-testid="pitches">
          {co.pitches!.map((pt) => {
            const head = pitchHeadStart(pt.stars);
            return (
              <div key={pt.id} className="card offer" data-testid="pitch">
                <img src={cover(pt.design.name, pt.design.theme, pt.design.mechanic, co.color, head.quality, 1, false, pt.design.audience)} width={48} height={36} alt="" style={{ imageRendering: "auto", borderRadius: 4, flex: "none" }} />
                <span className="stack offer-main">
                  <strong title={`${pt.designer} · ${t(C.mechanic[pt.design.mechanic].name)} × ${t(C.theme[pt.design.theme].name)}`}>{pt.design.name} <span style={{ color: "var(--gold)" }} aria-label={tp(pt.stars, "{n} star", "{n} stars")}>{"★".repeat(pt.stars)}</span></strong>
                  <span className="ivals">
                    <span>{t(C.audience[pt.design.audience].name)}</span>
                    <IconVal icon="build" label={t("{pct}% built", { pct: Math.round(head.prototype * 100) })}>{Math.round(head.prototype * 100)}%</IconVal>
                    <IconVal icon="coin" label={t("{pct}% royalty", { pct: Math.round(pt.royalty * 100) })}>{Math.round(pt.royalty * 100)}%</IconVal>
                    <IconVal icon="calendar" label={t("until {date}", { date: dateLabel(pt.expires) })}>{dateLabel(pt.expires)}</IconVal>
                  </span>
                </span>
                <button className="btn small primary" disabled={co.cash < pt.advance} onClick={() => void cmd({ type: "signPitch", pitchId: pt.id })} data-testid="sign-pitch">{t("Sign · {amount}", { amount: money(pt.advance) })}</button>
              </div>
            );
          })}
        </div>
      )}
      {list.length === 0 && <p className="muted">{tab === "needs" ? t("Nothing needs you.") : tab === "stock" ? t("No old stock.") : t("No games on sale yet.")}</p>}
      <table className="list products-list" style={{ marginTop: 10, display: list.length ? undefined : "none" }}>
        <thead><tr><th>{t("Game")}</th><th>{t("Score")}</th><th>{t("Sales")}</th><th>{t("Sold")}</th><th>{t("Stock")} <Why title={t("Stock")} lines={[{ label: t("Copies in stock, all games"), value: co.products.reduce((sum, x) => sum + x.stock, 0).toLocaleString("en-US") }, { label: t("Pallets used"), value: t("{a} of {b}", { a: palletsUsed(co), b: palletCapacity(co) }) }]} note={t("Copies on order count once they arrive.")} /></th><th>{t("Earned")} <Why title={t("Earned")} lines={[{ label: t("Your share of each sale"), value: "40–85%" }]} note={t("Shops keep the rest. The print screen shows each channel's share.")} /></th><th /></tr></thead>
        <tbody>
          {list.map((p) => {
            const w = game.week - p.releaseWeek;
            const lines = lineOptions(game, co, p);
            const when = p.launched ? `${dateLabel(p.releaseWeek)} · ${t(C.audience[p.design.audience].name)}` : t("Printing");
            return (
              <tr key={p.id} data-testid="product-row">
                <td className="pl-name">
                  <span className="row" style={{ gap: 8, minWidth: 0, flexWrap: "nowrap" }}>
                    <img src={cover(p.design.name, p.design.theme, p.design.mechanic, co.color, p.q.art || 0.6, 1, wonAward(game, p.design.name), p.design.audience)} width={48} height={36} alt="" style={{ imageRendering: "auto", borderRadius: 4, flex: "none" }} />
                    <span style={{ minWidth: 0 }}>
                      <strong className="pl-title" title={when}>{p.design.name}</strong>
                      {(!p.launched || p.evergreen || p.revivalWeek !== undefined || p.clearance) && (
                        <span className="tag-row">
                          {!p.launched && <span className="tag">{t("Printing")}</span>}
                          {p.evergreen && <span className="tag good" title={t("evergreen")}><Icon name="plant" size={11} /><span className="tag-word"> {t("evergreen")}</span></span>}
                          {p.revivalWeek !== undefined && <span className="tag good">{t("cult revival!")}</span>}
                          {p.clearance && <span className="tag bad">{t("on clearance")}</span>}
                        </span>
                      )}
                    </span>
                  </span>
                </td>
                <td className="pl-score">{p.launched ? <button className="res-btn" onClick={() => set({ screen: { kind: "reviews", productId: p.id } })} aria-label={t("Open reviews")}><ScoreBadge score={p.score} compact /></button> : "—"}</td>
                <td className="pl-spark"><span className="row" style={{ gap: 4, flexWrap: "nowrap" }}><Sparkline data={p.weekly} w={92} />{p.launched && <Why title={t("Why {name} sells what it does", { name: p.design.name })} lines={salesWhy(game, co, p).map((l) => whyLine(l, p))} note={t("Each line compares with an average game this week. Reviews, shops and price matter most; buzz fades after launch.")} />}</span></td>
                <td className="num pl-sold">
                  <IconVal glyph="sold" label={`${t("Sold")} ${p.sold.toLocaleString("en-US")}`}>{short(p.sold)}</IconVal>
                  {p.launched && cafe ? <IconVal icon="replay" label={t("{pct}% replay", { pct: Math.round(p.trueReplay * 100) })} className="pl-replay">{Math.round(p.trueReplay * 100)}%</IconVal> : null}
                </td>
                <td className="num pl-stock"><IconVal icon="box" label={`${t("Stock")} ${p.stock.toLocaleString("en-US")}`}>{short(p.stock)}</IconVal></td>
                <td className="num pl-earn"><IconVal icon="coin" label={t("Earned")}>{money(p.revenue)}</IconVal></td>
                <td className="pl-act">
                  <span className="product-actions">
                    {p.launched ? <button className="btn small" onClick={() => set({ screen: { kind: "print", productId: p.id } })} data-testid="reprint"><Icon name="print" size={14} />{t("Reprint")}</button> : <span />}
                    {lines.some((o) => o.ok) ? (
                      <select value="" onChange={(e) => e.target.value && set({ screen: { kind: "followup", productId: p.id, line: e.target.value as LineKind } })} aria-label={t("Grow the line")} data-testid="grow-line">
                        <option value="">{t("Follow-up…")}</option>
                        {lines.map((o) => <option key={o.kind} value={o.kind} disabled={!o.ok}>{t(LINE_LABEL[o.kind])}</option>)}
                      </select>
                    ) : <span />}
                    {p.meta ? <button className="btn small" onClick={() => set({ screen: { kind: "meta", productId: p.id } })} data-testid="open-meta">{t("Meta")}{p.meta.dominance > 0.62 ? " !" : ""}</button>
                      : p.launched && p.stock > 0 && !p.clearance && w > 26 ? <button className="btn small ghost" onClick={() => cmd({ type: "clearance", productId: p.id })} title={t("Sell the rest at 35% of the price for 8 weeks; anything left is pulped, so it stops costing storage.")}>{t("Clear out")}</button> : <span />}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Sheet>
  );
}

/** "Spring 1972" in the player's language. */
export function dateLabel(week: number): string {
  const d = dateOf(week);
  return `${t(d.season)} ${d.year}`;
}

/** salesWhy writes its lines in English (the rules engine): translate the known shapes, keep the numbers. */
function whyLine(l: { label: string; value: string }, p: Product): { label: string; value: string } {
  let label: string;
  let m: RegExpExecArray | null;
  if ((m = /^Reviews \((.+)\)$/.exec(l.label))) label = t("Reviews ({score})", { score: m[1] });
  else if ((m = /^Age \((\d+) weeks?\)$/.exec(l.label))) label = tp(Number(m[1]), "Age ({n} week)", "Age ({n} weeks)");
  else if (l.label.endsWith(" right now")) label = t("{theme} right now", { theme: t(C.theme[p.design.theme].name) });
  else label = t(l.label);
  let value = l.value;
  if ((m = /^(\d+)% of .+ players$/.exec(l.value))) value = t("{pct}% of {audience} players", { pct: m[1], audience: t(C.audience[p.design.audience].name).toLowerCase() });
  else if (/[a-z]/i.test(l.value)) value = t(l.value);
  return { label, value };
}
